import React, { useMemo, useState } from "react";


const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

const isRetailerMobile =
  typeof window !== "undefined" &&
  window.innerWidth < 640 &&
  authUser?.role === 5;

const glassCard =
  "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]";

/* ================== HELPERS ================== */
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function formatNumber(n) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function moneyINR(n) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function IconDot({ className }) {
  return <span className={cn("inline-block h-2.5 w-2.5 rounded-full", className)} />;
}

/* ================== DONUT SVG ================== */
function Donut({
  segments,
  size = 220,
  stroke = 22,
  activeKey,
  onPick,
  isRetailerMobile = false, // 👈 pass from parent
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = cx - stroke;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const circumference = 2 * Math.PI * r;

  let offset = 0;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto block">

      {/* ===== Background Ring ===== */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={
          isRetailerMobile
            ? "rgba(255,255,255,0.15)"
            : "rgb(241 245 249)"
        }
        strokeWidth={stroke}
      />

      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {segments.map((s) => {
          const dash = (s.value / total) * circumference;
          const isActive = activeKey === s.key;

          const circle = (
            <circle
              key={s.key}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={isActive ? stroke + 6 : stroke}
              strokeDasharray={`${dash} ${circumference}`}
              strokeDashoffset={-offset}
              opacity={
                activeKey
                  ? isActive
                    ? 1
                    : 0.25
                  : 1
              }
              onClick={() => onPick?.(s.key)}
              style={{
                cursor: "pointer",
                transition: "all 0.35s ease",
                filter:
                  isActive && isRetailerMobile
                    ? "drop-shadow(0 0 10px rgba(255,255,255,0.4))"
                    : "none",
              }}
            />
          );

          offset += dash;
          return circle;
        })}
      </g>

      {/* ===== Center Hole ===== */}
      <circle
        cx={cx}
        cy={cy}
        r={r - stroke}
        fill={
          isRetailerMobile
            ? "rgba(255,255,255,0.08)"
            : "white"
        }
        style={{
          backdropFilter: isRetailerMobile ? "blur(6px)" : "none",
        }}
      />
    </svg>
  );
}

/* ================== SKELETONS ================== */

function DonutSkeleton() {
  return (
    <div className="relative mx-auto w-[170px] sm:w-[200px] lg:w-[220px] animate-pulse">
      {/* ring */}
      <div className="aspect-square rounded-full border-[22px] border-slate-200" />

      {/* center text */}
      <div className="absolute inset-0 grid place-items-center">
        <div className="space-y-2 text-center">
          <div className="h-6 w-20 rounded bg-slate-200 mx-auto" />
          <div className="h-3 w-24 rounded bg-slate-200 mx-auto" />
        </div>
      </div>
    </div>
  );
}

function LegendSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl p-3 ring-1 ring-slate-200 bg-slate-50 space-y-2"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <div className="h-4 w-20 rounded bg-slate-300" />
            </div>
            <div className="h-4 w-14 rounded bg-slate-300" />
          </div>

          <div className="h-2 w-full rounded bg-slate-200" />
        </div>
      ))}
    </div>
  );
}


/* ================== MAIN COMPONENT ================== */
export default function StatusDonutChart({
  title = "Warranty Status",
  subtitle = "Active / Provisioning / Rejected",
  mode = "count", // "count" | "amount"
  data,
  loading,
}) {

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

const isRetailerMobile =
  typeof window !== "undefined" &&
  window.innerWidth < 640 &&
  (authUser?.role === 5 || authUser?.role === 6);

const glassCard =
  "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]";
  const fallback = { Active: 8, Provisioning: 3, Rejected: 2 };
  const source = data || fallback;

  const [picked, setPicked] = useState(null);

  const segments = useMemo(
    () => [
      {
        key: "Active",
        label: "Active",
        value: Number(source.Active || 0),
        color: "rgb(16 185 129)",
        dot: "bg-emerald-500",
      },
      {
        key: "Provisioning",
        label: "Provisioning",
        value: Number(source.Provisioning || 0),
        color: "rgb(245 158 11)",
        dot: "bg-amber-500",
      },
      {
        key: "Rejected",
        label: "Rejected",
        value: Number(source.Rejected || 0),
        color: "rgb(244 63 94)",
        dot: "bg-rose-500",
      },
    ],
    [source]
  );

  const total = useMemo(
    () => segments.reduce((a, s) => a + s.value, 0),
    [segments]
  );

  const pickedSeg = segments.find((s) => s.key === picked);

  const headline = mode === "amount"
    ? moneyINR(pickedSeg ? pickedSeg.value : total)
    : formatNumber(pickedSeg ? pickedSeg.value : total);

  const subline = pickedSeg
    ? pickedSeg.label
    : mode === "amount"
    ? "Total Earnings"
    : "Total Warranties";

  return (
    <div className="mx-auto ">
      {/* <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm overflow-hidden"> */}
      <div
        className={cn(
          "rounded-3xl p-4 sm:p-5 transition-all duration-300 overflow-hidden",
          isRetailerMobile
            ? glassCard
            : "border border-slate-200 bg-white shadow-sm"
        )}
      >
        {/* header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm sm:text-base font-extrabold text-slate-900">
              {title}
            </div>
            <div className="mt-0.5 text-xs sm:text-sm font-semibold text-slate-500">
              {subtitle}
            </div>
          </div>

          {/* <div className="flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200"> */}
            {/* <span className="text-[11px] font-extrabold text-slate-700">Mod</span> */}
            {/* <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-extrabold ring-1 ring-slate-200"> */}
            <span
              className={cn(
                "rounded-full px-2.5 py-1 text-[11px] font-extrabold transition-all",
                isRetailerMobile
                  ? "bg-white/20 text-black border border-white/30"
                  : "bg-white ring-1 ring-slate-200 text-slate-800"
              )}
            >
              {mode === "amount" ? "Amount" : "Count"}
            </span>
          {/* </div> */}
        </div>

        {/* body */}
        <div className="mt-6 flex flex-col lg:grid lg:grid-cols-2 gap-6">
          {loading ? (
            <>
              <DonutSkeleton />
              <LegendSkeleton />
            </>
          ) : (
            <>
              {/* donut */}
              <div className="relative mx-auto w-full max-w-[220px]">

                <Donut
                  segments={segments}
                  activeKey={picked}
                  onPick={(k) => setPicked(p => (p === k ? null : k))}
                  isRetailerMobile={isRetailerMobile}
                />

                {/* center text */}
                <div className="pointer-events-none absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-extrabold text-slate-900">
                      {headline}
                    </div>
                    <div className="text-xs font-semibold text-slate-500">
                      {subline}
                    </div>
                  </div>
                </div>
              </div>

              {/* legend */}
              <div className="space-y-2">
                {segments.map((s) => {
                  const pct = total ? Math.round((s.value / total) * 100) : 0;
                  const focused = picked === s.key;

                  return (
                    <button
                      key={s.key}
                      onClick={() => setPicked(p => (p === s.key ? null : s.key))}
                      className={cn(
                        "w-full rounded-2xl p-2.5 sm:p-3 text-left ring-1 transition",
                        focused
                          ? isRetailerMobile
                            ? "bg-white/20 text-black border border-white/40"
                            : "bg-blue-700 text-white ring-blue-700"
                          : isRetailerMobile
                          ? "bg-white/10 text-black/80 border border-white/20 hover:bg-white/20"
                          : "bg-slate-50 ring-slate-200 hover:bg-slate-100"
                      )}
                    >
                      <div className="flex justify-between items-center gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <IconDot className={s.dot} />
                          <div className="truncate text-sm font-extrabold">
                            {s.label}
                          </div>
                        </div>
                        <div className="text-sm font-extrabold">
                          {mode === "amount"
                            ? moneyINR(s.value)
                            : formatNumber(s.value)}
                        </div>
                      </div>

                      <div className="mt-2">
                        <div className="h-1.5 sm:h-2 rounded-full bg-white ring-1 ring-slate-200">
                          <div
                            className={cn(
                              "h-full rounded-full",
                              s.key === "Active"
                                ? "bg-emerald-500"
                                : s.key === "Provisioning"
                                ? "bg-amber-500"
                                : "bg-rose-500"
                            )}
                            style={{ width: clamp(pct, 0, 100) + "%" }}
                          />
                        </div>
                        <div className="mt-1 text-[11px] font-semibold">
                          {pct}% of total
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}





// import React, { useMemo, useState } from "react";

// /**
//  * Donut Chart (SVG) – Active / Provisioning / Rejected
//  * ✅ React + Tailwind (JavaScript)
//  * ✅ No external libraries
//  * ✅ Works with counts OR amounts
//  *
//  * Usage:
//  *   <StatusDonutChart />
//  * Or pass data:
//  *   <StatusDonutChart data={{ Active: 12, Provisioning: 3, Rejected: 2 }} title="Warranty Count" />
//  */

// function cn(...classes) {
//   return classes.filter(Boolean).join(" ");
// }

// function clamp(n, a, b) {
//   return Math.max(a, Math.min(b, n));
// }

// function formatNumber(n) {
//   const v = Number(n || 0);
//   try {
//     return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v);
//   } catch {
//     return String(Math.round(v));
//   }
// }

// function moneyINR(n) {
//   const v = Number(n || 0);
//   try {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       maximumFractionDigits: 0,
//     }).format(v);
//   } catch {
//     return `₹${Math.round(v).toLocaleString("en-IN")}`;
//   }
// }

// function polarToCartesian(cx, cy, r, angleDeg) {
//   const a = ((angleDeg - 90) * Math.PI) / 180;
//   return {
//     x: cx + r * Math.cos(a),
//     y: cy + r * Math.sin(a),
//   };
// }

// function describeArc(cx, cy, r, startAngle, endAngle) {
//   const start = polarToCartesian(cx, cy, r, endAngle);
//   const end = polarToCartesian(cx, cy, r, startAngle);
//   const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
//   return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
// }

// function IconDot({ className }) {
//   return <span className={cn("inline-block h-2.5 w-2.5 rounded-full", className)} />;
// }

// function Donut({ segments, size = 240, stroke = 20, activeKey, onPick }) {
//   const cx = size / 2;
//   const cy = size / 2;
//   const r = cx - stroke;
//   const total = segments.reduce((a, s) => a + s.value, 0) || 1;
//   const circumference = 2 * Math.PI * r;

//   let acc = 0;

//   return (
//     <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
//       {/* background track */}
//       <circle
//         cx={cx}
//         cy={cy}
//         r={r}
//         fill="none"
//         stroke="rgb(241 245 249)"
//         strokeWidth={stroke}
//       />

//       <g transform={`rotate(-90 ${cx} ${cy})`}>
//         {segments.map((s) => {
//           const pct = s.value / total;
//           const dash = pct * circumference;
//           const isActive = activeKey ? activeKey === s.key : false;

//           const el = (
//             <circle
//               key={s.key}
//               cx={cx}
//               cy={cy}
//               r={r}
//               fill="none"
//               stroke={s.color}
//               strokeWidth={isActive ? stroke + 6 : stroke}
//               strokeDasharray={`${dash} ${circumference - dash}`}
//               strokeDashoffset={-acc}
//               strokeLinecap="butt"
//               opacity={activeKey ? (isActive ? 1 : 0.25) : 1}
//               onClick={() => onPick?.(s.key)}
//               style={{ cursor: "pointer", transition: "all 0.3s ease" }}
//             />
//           );

//           acc += dash;
//           return el;
//         })}
//       </g>

//       {/* clean center */}
//       <circle cx={cx} cy={cy} r={r - stroke / 1.2} fill="white" />
//     </svg>
//   );
// }

// export default function StatusDonutChart({
//   title = "Warranty Status",
//   subtitle = "Active / Provisioning / Rejected",
//   mode = "count", // 'count' | 'amount'
//   data,
//   size = 220,
// }) {
//   // Default demo data
//   const fallback = { Active: 8, Provisioning: 3, Rejected: 2 };
//   const source = data || fallback;

//   const [picked, setPicked] = useState(null);

//   const segments = useMemo(() => {
//     const items = [
//       {
//         key: "Active",
//         label: "Active",
//         value: Number(source.Active || 0),
//         color: "rgb(16 185 129)",
//         dot: "bg-emerald-500",
//       },
//       {
//         key: "Provisioning",
//         label: "Provisioning",
//         value: Number(source.Provisioning || 0),
//         color: "rgb(245 158 11)",
//         dot: "bg-amber-500",
//       },
//       {
//         key: "Rejected",
//         label: "Rejected",
//         value: Number(source.Rejected || 0),
//         color: "rgb(244 63 94)",
//         dot: "bg-rose-500",
//       },
//     ];
//     return items;
//   }, [source]);

//   const total = useMemo(() => segments.reduce((a, s) => a + s.value, 0), [segments]);

//   const responsiveSize =
//   typeof window !== "undefined" && window.innerWidth < 640
//     ? 180
//     : window.innerWidth < 1024
//     ? 200
//     : 220;

//   const pickedSeg = useMemo(() => {
//     if (!picked) return null;
//     return segments.find((s) => s.key === picked) || null;
//   }, [picked, segments]);

//   const headline = useMemo(() => {
//     const v = pickedSeg ? pickedSeg.value : total;
//     if (mode === "amount") return moneyINR(v);
//     return formatNumber(v);
//   }, [pickedSeg, total, mode]);

//   const subline = useMemo(() => {
//     if (pickedSeg) return pickedSeg.label;
//     return mode === "amount" ? "Total Earnings" : "Total Warranties";
//   }, [pickedSeg, mode]);

//   return (
//       <div className="mx-auto max-w-xl w-full">
//         <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
//           <div className="flex items-start justify-between gap-3">
//             <div>
//               <div className="text-sm sm:text-base font-extrabold text-slate-900">{title}</div>
//               <div className="mt-0.5 text-xs sm:text-sm font-semibold text-slate-500">{subtitle}</div>
//             </div>

//             <div className="flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200">
//               <span className="text-[11px] font-extrabold text-slate-700">Mode</span>
//               <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-extrabold text-slate-900 ring-1 ring-slate-200">
//                 {mode === "amount" ? "Amount" : "Count"}
//               </span>
//             </div>
//           </div>

//           <div className="mt-5 grid gap-6 grid-cols-1 md:grid-cols-2 items-center">
//             <div className="relative mx-auto flex justify-center">
//               <Donut segments={segments} size={responsiveSize} stroke={30} gapPx={12} activeKey={picked} onPick={(k) => setPicked((p) => (p === k ? null : k))} />

//               {/* Center label */}
//               <div className="pointer-events-none absolute inset-0 grid place-items-center">
//                 <div className="text-center">
//                   <div className="text-2xl font-extrabold tracking-tight text-slate-900">{headline}</div>
//                   <div className="mt-0.5 text-xs font-semibold text-slate-500">{subline}</div>
//                 </div>
//               </div>
//             </div>

//             <div>
//               <div className="space-y-2">
//                 {segments.map((s) => {
//                   const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
//                   const focused = picked ? picked === s.key : false;
//                   return (
//                     <button
//                       key={s.key}
//                       onClick={() => setPicked((p) => (p === s.key ? null : s.key))}
//                       className={cn(
//                         "w-full rounded-2xl p-2.5 sm:p-3 text-left ring-1 transition",
//                         focused ? "bg-blue-700 text-white ring-blue-700" : "bg-slate-50 text-blue-700 ring-slate-200 hover:bg-slate-100"
//                       )}
//                     >
//                       <div className="flex items-center justify-between gap-3">
//                         <div className="flex items-center gap-2 min-w-0">
//                           <IconDot className={cn(s.dot, focused ? "opacity-95" : "opacity-100")} />
//                           <div className="truncate text-sm font-extrabold">{s.label}</div>
//                         </div>
//                         <div className={cn("shrink-0 text-sm font-extrabold", focused ? "text-white" : "text-slate-900")}>
//                           {mode === "amount" ? moneyINR(s.value) : formatNumber(s.value)}
//                         </div>
//                       </div>
//                       <div className="mt-2">
//                         <div className={cn("h-1.5 sm:h-2 w-full rounded-full", focused ? "bg-white/15" : "bg-white ring-1 ring-slate-200")}>
//                           <div
//                             className={cn("h-2 rounded-full", focused ? "bg-white" : s.key === "Active" ? "bg-emerald-500" : s.key === "Provisioning" ? "bg-amber-500" : "bg-rose-500")}
//                             style={{ width: clamp(pct, 0, 100) + "%" }}
//                           />
//                         </div>
//                         <div className={cn("mt-1 text-[11px] font-semibold", focused ? "text-white/80" : "text-slate-500")}>
//                           {pct}% of total
//                         </div>
//                       </div>
//                     </button>
//                   );
//                 })}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//   );
// }
